import { useState } from "react";
import type { Album } from "@shared/types";
import { ApiError, createAlbum, scanAlbum } from "../api";
import { AlbumConfirm } from "./AlbumConfirm";

export function ScanAlbumTab() {
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Album | null>(null);
  const [savedTitle, setSavedTitle] = useState<string | null>(null);

  const submit = async (raw: string) => {
    const barcode = raw.replace(/\D/g, "");
    setPending(true);
    setError(null);
    try {
      const r = await scanAlbum(barcode);
      setResult(r.album);
      setInput("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Scan failed");
    } finally {
      setPending(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    setSavedTitle(null);
    const digits = val.replace(/\D/g, "");
    // UPC-A is 12 digits; some EAN-13 also work. Auto-submit on either.
    if (/^\d{12,13}$/.test(digits)) {
      submit(digits);
    }
  };

  const onSave = async (album: Album) => {
    try {
      const saved = await createAlbum(album);
      setResult(null);
      setSavedTitle(saved.title);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed");
    }
  };

  if (result) {
    return <AlbumConfirm album={result} onSave={onSave} onCancel={() => setResult(null)} />;
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm" htmlFor="barcode">
        Scan barcode (UPC)
      </label>
      <input
        id="barcode"
        type="text"
        inputMode="numeric"
        autoFocus
        autoComplete="off"
        value={input}
        onChange={onChange}
        placeholder="012345678901"
        className="w-full px-4 py-3 text-lg bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {pending && <p className="text-sm text-gray-400">Looking up…</p>}
      {error && (
        <div role="alert" className="text-sm text-red-400">
          {error}
        </div>
      )}
      {savedTitle && <div className="text-sm text-green-400">Saved: {savedTitle}</div>}
    </div>
  );
}
